import type { CalendarEvent, Highlight } from "@akem/shared";

export type MarketIndexSnapshot = {
  id: string;
  name: string;
  symbol: string;
  level: number;
  changePercent: number;
  intradaySparkline: number[];
};

export type SectorPerformance = {
  id: string;
  name: string;
  changePercent: number;
  leaders: string[];
  laggards: string[];
};

export type PortfolioHoldingSummary = {
  symbol: string;
  weight: number;
  changePercent: number;
};

export type PortfolioOverview = {
  id: string;
  name: string;
  totalValue: number;
  changePercent: number;
  holdings: PortfolioHoldingSummary[];
};

export type SecondOrderDomain =
  | "customers"
  | "suppliers"
  | "marketTrends"
  | "politics"
  | "regulatory"
  | "mna"
  | "logistics"
  | "themes";

export type DomainSummary = {
  domain: SecondOrderDomain;
  headline: string;
  sentimentScore: number;
  changePercent: number;
  bulletPoints: string[];
};

const now = Date.now();

export const mockCalendarEvents: CalendarEvent[] = [
  {
    id: "cal-earnings-nvda",
    ts: now + 3 * 24 * 60 * 60 * 1000,
    type: "earnings",
    label: "NVDA Q1 FY26 Earnings",
    link: "https://investor.nvidia.com",
    metadata: {
      symbol: "NVDA",
      reportTime: "post-market",
      fiscalPeriod: "Q1",
      fiscalYear: 2026,
      consensusEPS: 0.68,
    },
  },
  {
    id: "cal-fed-minutes",
    ts: now + 5 * 24 * 60 * 60 * 1000,
    type: "event",
    label: "FOMC Minutes Release",
    link: "https://www.federalreserve.gov",
    metadata: {
      importance: "high" as const,
      country: "US",
      source: "Federal Reserve",
    },
  },
  {
    id: "cal-cpi",
    ts: now + 8 * 24 * 60 * 60 * 1000,
    type: "event",
    label: "US CPI (May)",
    metadata: {
      importance: "high" as const,
      country: "US",
      source: "Bureau of Labor Statistics",
      forecast: 3.4,
      previous: 3.5,
      unit: "%",
    },
  },
  {
    id: "cal-dividend-msft",
    ts: now + 10 * 24 * 60 * 60 * 1000,
    type: "dividend",
    label: "MSFT Dividend Ex-Date",
    metadata: {
      symbol: "MSFT",
      amount: 0.75,
      currency: "USD",
      frequency: "quarterly",
    },
  },
  {
    id: "cal-earnings-cost",
    ts: now + 14 * 24 * 60 * 60 * 1000,
    type: "earnings",
    label: "COST Q3 FY25 Earnings",
    metadata: {
      symbol: "COST",
      reportTime: "post-market",
      fiscalPeriod: "Q3",
      fiscalYear: 2025,
      consensusEPS: 3.52,
    },
  },
];

export const mockHighlights: Highlight[] = [
  {
    id: "hl-supply-ai",
    ts: now - 30 * 60 * 1000,
    direction: 1,
    magnitude: 0.7,
    title: "AI supply chain easing unlocks accelerator shipments",
    summary:
      "Taiwan suppliers report 18% m/m capacity lift with expedited GPU substrates.",
    kind: "impact",
    relatedSymbols: ["TSM", "NVDA", "ASML"],
  },
  {
    id: "hl-political-trade",
    ts: now - 75 * 60 * 1000,
    direction: 0,
    magnitude: 0.4,
    title: "US-EU trade corridor review incoming",
    summary:
      "Commerce officials signal relaxed dual-use export controls for clean tech components.",
    kind: "news",
    relatedSymbols: ["GE", "FSLR"],
  },
  {
    id: "hl-logistics-ports",
    ts: now - 2 * 60 * 60 * 1000,
    direction: -1,
    magnitude: 0.6,
    title: "Port backups widen in LA/LB terminals",
    summary:
      "Stacked containers trigger 36-hour delays; transpacific spot rates jump 11% w/w.",
    kind: "impact",
    relatedSymbols: ["FDX", "ZIM"],
  },
  {
    id: "hl-customer-renewals",
    ts: now - 3 * 60 * 60 * 1000,
    direction: 1,
    magnitude: 0.5,
    title: "Enterprise renewals stabilize cloud workloads",
    summary:
      "Top SaaS vendors highlight 97% gross retention with AI copilots bundled into seats.",
    kind: "theme",
    relatedSymbols: ["MSFT", "CRM"],
  },
  {
    id: "hl-supplier-shifts",
    ts: now - 4 * 60 * 60 * 1000,
    direction: 1,
    magnitude: 0.45,
    title: "Power management suppliers chase AI edge nodes",
    summary:
      "PMIC vendors see bookings acceleration; Taiwan hub quoting 6% price lift for Q3.",
    kind: "impact",
    relatedSymbols: ["ON", "TXN"],
  },
  {
    id: "hl-mna-semis",
    ts: now - 6 * 60 * 60 * 1000,
    direction: 1,
    magnitude: 0.55,
    title: "Specialty logic merger chatter resurfaces",
    summary:
      "Private equity consortium revisits analog consolidation; advisors engaged for summer review.",
    kind: "relationship",
    relatedSymbols: ["ADI", "MCHP"],
  },
];

export const mockIndices: MarketIndexSnapshot[] = [
  {
    id: "dji",
    name: "Dow Jones Industrial Average",
    symbol: "DJI",
    level: 38245.62,
    changePercent: 0.62,
    intradaySparkline: [0, 0.4, 0.6, 0.3, 0.8, 0.9, 0.7],
  },
  {
    id: "spx",
    name: "S&P 500",
    symbol: "SPX",
    level: 5321.11,
    changePercent: 0.84,
    intradaySparkline: [0, 0.2, 0.5, 0.9, 1.1, 0.8, 1.0],
  },
  {
    id: "ndx",
    name: "NASDAQ 100",
    symbol: "NDX",
    level: 18945.4,
    changePercent: 1.26,
    intradaySparkline: [0, 0.5, 1.2, 1.0, 1.6, 1.9, 1.7],
  },
  {
    id: "rut",
    name: "Russell 2000",
    symbol: "RUT",
    level: 2076.8,
    changePercent: 0.35,
    intradaySparkline: [0, 0.1, 0.3, 0.15, 0.25, 0.4, 0.35],
  },
];

export const mockSectors: SectorPerformance[] = [
  {
    id: "technology",
    name: "Technology",
    changePercent: 1.24,
    leaders: ["NVDA", "MSFT", "AVGO"],
    laggards: ["CRM"],
  },
  {
    id: "industrials",
    name: "Industrials",
    changePercent: 0.88,
    leaders: ["CAT", "DE", "GE"],
    laggards: ["UPS"],
  },
  {
    id: "financials",
    name: "Financials",
    changePercent: -0.12,
    leaders: ["GS", "MS"],
    laggards: ["SCHW", "USB"],
  },
  {
    id: "healthcare",
    name: "Healthcare",
    changePercent: 0.41,
    leaders: ["LLY", "UNH"],
    laggards: ["PFE"],
  },
  {
    id: "energy",
    name: "Energy",
    changePercent: -0.35,
    leaders: ["XOM"],
    laggards: ["SLB", "HES"],
  },
  {
    id: "consumer",
    name: "Consumer Discretionary",
    changePercent: 0.56,
    leaders: ["TSLA", "HD"],
    laggards: ["NKE"],
  },
];

export const mockPortfolios: PortfolioOverview[] = [
  {
    id: "growth",
    name: "Growth Momentum",
    totalValue: 1250_000,
    changePercent: 0.85,
    holdings: [
      { symbol: "NVDA", weight: 0.18, changePercent: 1.12 },
      { symbol: "MSFT", weight: 0.16, changePercent: 0.74 },
      { symbol: "ASML", weight: 0.12, changePercent: 0.98 },
      { symbol: "NOW", weight: 0.09, changePercent: 0.66 },
    ],
  },
  {
    id: "dividend",
    name: "Dividend Stability",
    totalValue: 980_000,
    changePercent: 0.28,
    holdings: [
      { symbol: "PG", weight: 0.15, changePercent: 0.22 },
      { symbol: "KO", weight: 0.14, changePercent: 0.18 },
      { symbol: "PEP", weight: 0.13, changePercent: 0.25 },
      { symbol: "JNJ", weight: 0.12, changePercent: 0.19 },
    ],
  },
  {
    id: "infrastructure",
    name: "Infrastructure Rebuild",
    totalValue: 740_000,
    changePercent: 0.64,
    holdings: [
      { symbol: "CAT", weight: 0.17, changePercent: 0.72 },
      { symbol: "DE", weight: 0.15, changePercent: 0.68 },
      { symbol: "VMC", weight: 0.12, changePercent: 0.53 },
      { symbol: "URI", weight: 0.1, changePercent: 0.61 },
    ],
  },
];

export const mockDomainSummaries: Record<SecondOrderDomain, DomainSummary> = {
  customers: {
    domain: "customers",
    headline: "Enterprise demand steadies as AI copilots boost renewals",
    sentimentScore: 0.64,
    changePercent: 0.12,
    bulletPoints: [
      "Top-20 customers signal multi-year commitments through FY27",
      "Net revenue retention climbs 140 bps q/q with seat expansions",
    ],
  },
  suppliers: {
    domain: "suppliers",
    headline: "Component lead times compress back to 10-week baseline",
    sentimentScore: 0.58,
    changePercent: 0.18,
    bulletPoints: [
      "Substrate capacity additions pull-in 6 weeks ahead of plan",
      "Inventory burn accelerates among analog peers, easing spot pricing",
    ],
  },
  marketTrends: {
    domain: "marketTrends",
    headline: "Edge AI deployments jump 28% q/q across industrial clients",
    sentimentScore: 0.71,
    changePercent: 0.26,
    bulletPoints: [
      "Utilities pilot predictive maintenance with embedded vision stacks",
      "Retail POS upgrades pair LLM assistance with local inference nodes",
    ],
  },
  politics: {
    domain: "politics",
    headline: "Trade corridors stabilize as US-EU semiconductor pact advances",
    sentimentScore: 0.35,
    changePercent: -0.04,
    bulletPoints: [
      "Tariff exemptions extended for advanced packaging equipment",
      "Lawmakers target harmonized export disclosures for AI accelerators",
    ],
  },
  regulatory: {
    domain: "regulatory",
    headline: "SEC climate disclosure pause buys time for FY25 reporting build",
    sentimentScore: 0.22,
    changePercent: -0.07,
    bulletPoints: [
      "Pending IFRS guidance still expected to require scope 3 metrics",
      "Cyber disclosure amendments take effect for fiscal years starting Q4",
    ],
  },
  mna: {
    domain: "mna",
    headline:
      "Specialty logic tie-up chatter resurfaces among private equity groups",
    sentimentScore: 0.48,
    changePercent: 0.14,
    bulletPoints: [
      "Cross-border filings hint at early Hart-Scott-Rodino preparation",
      "Board-level diligence focuses on share-based comp harmonization",
    ],
  },
  logistics: {
    domain: "logistics",
    headline:
      "West coast port congestion reintroduces buffer inventory planning",
    sentimentScore: -0.32,
    changePercent: -0.21,
    bulletPoints: [
      "Air cargo bids up 11% w/w as shippers hedge contract exposure",
      "Ocean carriers reroute via Prince Rupert to absorb volume spikes",
    ],
  },
  themes: {
    domain: "themes",
    headline: "Sustainable datacenter retrofits become top-three capex item",
    sentimentScore: 0.54,
    changePercent: 0.19,
    bulletPoints: [
      "Hyperscalers co-invest in onsite battery systems to smooth loads",
      "Heat recapture pilots expand across Nordics with industrial partners",
    ],
  },
};
